/**
 * AdminServiceCitiesCard — admin-only CRUD on the canonical service-city list.
 * The list controls:
 *   - The dropdown shown to vendors/pros/venues during KYC (base city) +
 *     "Serving Cities" dashboard card.
 *   - The dropdown shown to customers on Instant Pricing (event_city).
 *
 * Customers / vendors cannot type a city manually — they must pick from this list.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MapPin, Plus, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminServiceCitiesCard = () => {
  const [cities, setCities] = useState([]);
  const [newCity, setNewCity] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchCities(); }, []);

  const fetchCities = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/service-cities`, { withCredentials: true });
      setCities(data.cities || []);
    } catch (err) {
      console.error('Failed to load service cities', err);
    }
  };

  const addCity = async () => {
    const name = (newCity || '').trim();
    if (!name) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/service-cities`,
        { name }, { withCredentials: true });
      setCities(data.cities || []);
      if (data.added) toast.success(`Added "${name}"`);
      else toast.info('Already in the list');
      setNewCity('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add');
    }
    setSaving(false);
  };

  const removeCity = async (name) => {
    if (!window.confirm(`Remove "${name}"? Existing vendors that selected it will keep it but new pickers won't see it.`)) return;
    setSaving(true);
    try {
      const { data } = await axios.delete(`${BACKEND_URL}/api/admin/service-cities/${encodeURIComponent(name)}`,
        { withCredentials: true });
      setCities(data.cities || []);
      toast.success(`Removed "${name}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove');
    }
    setSaving(false);
  };

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="admin-service-cities-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <MapPin size={20} className="text-brand-primary" /> Service Cities (Geofence)
        </CardTitle>
        <CardDescription>
          The canonical list of cities BeeWeddy serves. Used by every dropdown across the platform — customers
          picking <span className="font-semibold">event city</span>, vendors picking <span className="font-semibold">base city</span> in KYC,
          and "Serving Cities" cards on dashboards. Vendors are auto-matched to customer events whose city is in
          either their KYC base city or their additional serving cities.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2 items-end mb-4">
          <div className="flex-1">
            <Label className="text-xs font-bold text-text-secondary">Add a city</Label>
            <Input type="text" value={newCity}
              onChange={(e) => setNewCity(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCity(); } }}
              placeholder="e.g. Madurai"
              className="rounded-none border-[#E8E1D5] h-9"
              data-testid="admin-city-input" />
          </div>
          <Button onClick={addCity} disabled={!newCity.trim() || saving}
            className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50"
            data-testid="admin-city-add-btn">
            <Plus size={16} className="mr-1" /> Add
          </Button>
        </div>

        <div className="border border-[#E8E1D5] rounded p-3">
          <p className="text-xs font-bold tracking-wider uppercase text-text-secondary mb-2">
            Active Cities ({cities.length})
          </p>
          {cities.length === 0 ? (
            <p className="text-sm text-text-secondary italic">No cities yet. Add at least one before vendors can submit KYC.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {cities.map((c) => (
                <span key={c} className="inline-flex items-center gap-1.5 bg-brand-primary/10 border border-brand-primary/30 text-brand-primary px-3 py-1.5 rounded-full text-sm font-medium"
                  data-testid={`admin-city-chip-${c}`}>
                  <MapPin size={12} /> {c}
                  <button type="button" onClick={() => removeCity(c)} disabled={saving}
                    className="ml-1 hover:bg-brand-primary/20 rounded-full p-0.5"
                    data-testid={`admin-city-remove-${c}`}>
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminServiceCitiesCard;
